import React, { useCallback, type FC } from 'react'

import {
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  LINK_VARIANT_HIGHLIGHT,
  Link,
  NoResults,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TEXT_VARIANT_LIGHT,
  Text,
} from 'mailup-web-components'
import { useIntl } from 'react-intl'
import { useRecoilValue } from 'recoil'

import {
  resultsState,
  searchState,
} from 'Pages/GroupsOverlap/recoil/state/state'
import { useNavigate } from 'react-router-dom'

const Results: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()

  const { to } = useRecoilValue(searchState)
  const results = useRecoilValue(resultsState)

  const handleClick = useCallback(
    (idGroup: number) => () => {
      navigate(`/recipients/list?sch=email&ssst=optin&sig=${idGroup}`)
    },
    [navigate],
  )

  const handleOverlapClick = useCallback(
    (idGroup: number, idGroup2: number) => () => {
      navigate(
        `/recipients/list?sch=email&ssst=optin&sig=${idGroup}&sig2=${idGroup2}`,
      )
    },
    [navigate],
  )

  if (results.status === 'initial') return null

  const heads = [
    {
      field: 'IdName',
      label: intl.formatMessage({ id: 'Load.GroupsOverlap.GroupName' }),
      span: 4,
    },
    {
      field: 'Subscribers',
      label: intl.formatMessage({ id: 'Load.Common.EmailSubscribers' }),
      span: 4,
      align: 'right',
    },
    {
      field: 'Overlapping',
      label: intl.formatMessage({
        id: 'Load.GroupsOverlap.RecipientsOverlapping',
      }),
      span: 4,
      align: 'right',
    },
  ]

  return (
    <Panel data-mu-test="groups-overlap__results" margin="0">
      <DataListHeader heads={heads} />

      {results.status === 'loading' &&
        Array.from({ length: to.length }).map((_, idx) => (
          <DataListRow key={idx}>
            <Grid cols={12}>
              <GridCell span={heads[0].span}>
                <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
              </GridCell>
              <GridCell span={heads[1].span}>
                <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
              </GridCell>
              <GridCell span={heads[2].span}>
                <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
              </GridCell>
            </Grid>
          </DataListRow>
        ))}

      {results.status === 'ready' && results.items.length === 0 && (
        <NoResults>
          {intl.formatMessage({ id: 'Load.Common.NoResultChosen' })}
        </NoResults>
      )}

      {results.status === 'ready' && results.items.length > 0 && (
        <section>
          {results.items.map((g) => (
            <DataListRow key={g.id}>
              <Grid cols={12}>
                <GridCell span={heads[0].span}>
                  <Text variant={TEXT_VARIANT_LIGHT}>
                    {g.id} - {g.name}
                  </Text>
                </GridCell>
                <GridCell span={heads[1].span}>
                  <Flex justifyContent="end">
                    <Link
                      variant={LINK_VARIANT_HIGHLIGHT}
                      data-mu-test="group__subscribers"
                      onClick={handleClick(g.id)}
                    >
                      {intl.formatNumber(g.subscribers)}
                    </Link>
                  </Flex>
                </GridCell>
                <GridCell span={heads[2].span}>
                  <Flex justifyContent="end">
                    <Link
                      variant={LINK_VARIANT_HIGHLIGHT}
                      data-mu-test="group__overlapping"
                      onClick={handleOverlapClick(g.id, g.idGroupFrom)}
                    >
                      {intl.formatNumber(g.overlap)}
                    </Link>
                  </Flex>
                </GridCell>
              </Grid>
            </DataListRow>
          ))}
        </section>
      )}
    </Panel>
  )
}

export default Results
